import { JMCButton } from "@components/JMCButton/JMCButton";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { Hero } from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import { Colors } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { CMSButtonProps, CMSFootnotes, CMSHeroProps } from "@types";
import { default as cmsMapper } from "@utils/cms-mapper";
import { GatsbyImage } from "gatsby-plugin-image";
import React from "react";

import style from "./style.module.scss";

interface Props {
    hero: CMSHeroProps;
    footnotes?: CMSFootnotes[];
    height?: string;
    placement?: string;
    titleComponent?: React.ElementType;
    loading?: "eager" | "lazy";
    id?: string;
    titleVariant?: "h1" | "h2" | "h3";
    marginXl?: boolean;
}

export const JMCHero: React.FunctionComponent<Props> = ({
    hero,
    footnotes,
    height = null,
    placement,
    titleComponent = "h3",
    loading = "lazy",
    id = undefined,
    titleVariant = "h3",
    marginXl = true,
}: Props) => {
    const { image, ...heroProps } = cmsMapper.mapHeroProps(hero);

    const footer = hero?.call_to_action?.map(({ button }: { button: CMSButtonProps }, index: number) => {
        return <JMCButton key={index} button={button} footnotes={footnotes} placement={placement} />;
    });

    const heroTextColor = (): Colors => {
        if (hero?.display_options?.body_color) {
            return hero.display_options?.body_color === "--primary" ||
                hero.display_options?.body_color === "--secondary"
                ? ((hero.display_options?.body_color.replace("--", "") + "-general") as Colors)
                : (hero.display_options?.body_color.replace("--", "") as Colors);
        } else {
            return "white";
        }
    };

    const heroTitle = (
        <div style={{ zIndex: 100 }}>
            {typeof hero?.display_title === "string" ? (
                <JMCHtml footnotes={footnotes}>{hero.display_title}</JMCHtml>
            ) : (
                hero?.display_title
            )}
        </div>
    );

    return (
        <div className={style.heroWrapper}>
            <Hero
                {...heroProps}
                id={id || heroProps.id}
                title={hero?.display_title != "" ? heroTitle : ""}
                footer={footer}
                height={height}
                marginXl={marginXl}
                titleComponent={titleComponent}
                titleVariant={titleVariant}
            >
                {hero?.hero_body && (
                    <div style={{ zIndex: 100 }}>
                        <JMCHtml footnotes={footnotes} color={heroTextColor()} placement={placement}>
                            {hero.hero_body}
                        </JMCHtml>
                    </div>
                )}
            </Hero>
            {hero?.background_image?.gatsbyImageData ? (
                <GatsbyImage
                    image={hero?.background_image?.gatsbyImageData}
                    alt={
                        typeof hero.display_title === "string" ? hero.display_title : hero.display_title?.children || ""
                    }
                    loading={loading}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        insetInlineStart: 0,
                    }}
                    imgStyle={{ objectPosition: heroProps.imagePosition.replace("-", " ") }}
                />
            ) : image ? (
                <img
                    src={image}
                    loading={"eager"}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        insetInlineStart: 0,
                        objectFit: "cover",
                    }}
                ></img>
            ) : null}
        </div>
    );
};
